import { useState, type ReactNode } from 'react'
import { TXT, MONO, PLATFORM_LABEL, RtmpIcon } from '../../live-ui/ui'
import { Button, Chip, Icon, type IconName } from '../../ui'
import type { Platform } from '../../live-ui/types'
import type { Spec } from './states'
import type { RelayRole } from './api'

/*
 * Console primitives (design/network-console/proto/src/ui/kit.tsx), on top of
 * the kit already in web/ (live-ui/ui.tsx). Stage tags of the prototype are
 * not carried over: R0 shows only what is real.
 */

/**
 * Badge of a state: the system's chip — icon + word + border style per state (StateSystem, AUDIT
 * CN-02: warn dashed, fail double, idle dotted, start / live solid). The spec's glyph is not drawn.
 */
export function StateBadge({ spec, text, title, testId }: { spec: Spec; text?: string; title?: string; testId?: string }) {
  return (
    <span title={title ?? spec.hint} className="inline-flex" data-testid={testId} data-state={spec.word}>
      <Chip state={spec.color} className="flex-none">{text ?? spec.word}</Chip>
    </span>
  )
}

/** The same state as inline text (dense cells): the state icon and the word in its colour. */
export function StateText({ spec, text, className = '', testId }: { spec: Spec; text?: string; className?: string; testId?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold tracking-[.06em] whitespace-nowrap ${TXT[spec.color]} ${className}`} title={spec.hint} data-testid={testId} data-state={spec.word}>
      <Icon name={`state-${spec.color}`} size={12} className={spec.color === 'start' ? 'sx-turn' : undefined} />
      <span>{text ?? spec.word}</span>
    </span>
  )
}

/* ── role glyphs: the shape reads without colour ─────────────────────────── */

const ROLE_PATH: Record<RelayRole, string> = {
  ingress: 'M1 1 L10 6 L1 11 Z',
  relay: 'M1 6 L6 1 L11 6 L6 11 Z',
  egress: 'M1 1 L8 6 L1 11 Z M10 1 V11',
  hub: 'M3.5 1 H8.5 L11.5 6 L8.5 11 H3.5 L0.5 6 Z',
  integrations: 'M6 1 A5 5 0 1 1 5.99 1 Z M3.5 6 H8.5',
}
export const ROLE_LABEL: Record<RelayRole, string> = { ingress: 'Ingress', relay: 'Relay', egress: 'Egress', hub: 'Hub', integrations: 'Integrations' }
export const ROLE_HINT: Record<RelayRole, string> = {
  ingress: 'Ingress: holds the OBS connection',
  relay: 'Relay: forwards streams between nodes',
  egress: 'Egress: holds platform sessions',
  hub: 'Hub: center node for via-center streams',
  integrations: 'Integrations: platform APIs and chat, no media',
}

export function RoleGlyph({ role, size = 12, className = '' }: { role: RelayRole; size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" aria-hidden className={`flex-none ${className}`}>
      <path d={ROLE_PATH[role]} fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  )
}

/* ── copyable mono value (IDs, SPKI, addresses): one click, never a secret ─ */

export function Copy({ value, display, label, className = '', testId }: { value: string; display?: string; label: string; className?: string; testId?: string }) {
  const [done, setDone] = useState(false)
  return (
    <span className={`inline-flex max-w-full min-w-0 items-center gap-1.5 ${className}`} data-testid={testId}>
      <span className="num truncate font-mono text-xs text-text-soft" title={value}>
        {display ?? value}
      </span>
      <button
        type="button"
        aria-label={`Copy ${label}`}
        title={done ? 'Copied' : `Copy ${label}`}
        onClick={() => {
          void navigator.clipboard?.writeText(value).catch(() => undefined)
          setDone(true)
          window.setTimeout(() => setDone(false), 1500)
        }}
        className={`grid h-6 min-w-6 flex-none place-items-center rounded-sm border px-1 font-mono text-[10px] ${done ? 'border-live/50 text-live' : 'border-line-2 text-muted hover:text-text'}`}
      >
        {done ? (
          '✓'
        ) : (
          <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden>
            <rect x="3.5" y="3.5" width="7" height="7" rx="1" fill="none" stroke="currentColor" />
            <path d="M8.5 1.5h-6a1 1 0 0 0-1 1v6" fill="none" stroke="currentColor" />
          </svg>
        )}
      </button>
    </span>
  )
}

/* ── layout pieces ────────────────────────────────────────────────────────── */

export function NetPanel({ title, aside, children, className = '', pad = true, id, testId }: {
  title?: ReactNode
  aside?: ReactNode
  children: ReactNode
  className?: string
  pad?: boolean
  id?: string
  testId?: string
}) {
  return (
    <section id={id} data-testid={testId} className={`rs-corners min-w-0 rounded-lg border border-line ${className}`}>
      {(title || aside) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
          {typeof title === 'string' ? <h2 className="text-[15px] font-semibold">{title}</h2> : title}
          {aside && <div className="flex flex-wrap items-center gap-2">{aside}</div>}
        </header>
      )}
      <div className={pad ? 'p-4' : ''}>{children}</div>
    </section>
  )
}

/** Small "label: value". */
export function Fact({ k, children, className = '', testId }: { k: string; children: ReactNode; className?: string; testId?: string }) {
  return (
    <div className={`flex min-w-0 flex-col gap-0.5 ${className}`} data-testid={testId}>
      <span className="mono-label uppercase">{k}</span>
      <span className="min-w-0 text-sm">{children}</span>
    </div>
  )
}

export function EpochTag({ e, className = '' }: { e: number; className?: string }) {
  return <span className={`num rounded-md border border-line-2 px-1.5 font-mono text-[11px] leading-5 whitespace-nowrap text-text-soft ${className}`}>EPOCH e{e}</span>
}

/** Nothing here yet (EmptyStates): the dashed frame, an icon, what is missing and the one thing to do. */
export function Empty({ title, children, action, testId, icon = 'node' }: { title: string; children?: ReactNode; action?: ReactNode; testId?: string; icon?: IconName }) {
  return (
    <div data-testid={testId} className="sx-empty">
      <div className="ic">
        <Icon name={icon} size={24} />
      </div>
      <h4>{title}</h4>
      {children && <div className="max-w-[60ch] text-[13px] leading-[18px] text-muted">{children}</div>}
      {action}
    </div>
  )
}

/** A request failed: the fail state (icon, word, double edge), the error and a retry. */
export function ErrorBox({ text, onRetry, busy, testId }: { text: string; onRetry?: () => void; busy?: boolean; testId?: string }) {
  return (
    <div role="alert" data-testid={testId} className="flex flex-wrap items-center gap-3 rounded-md border-[3px] border-double border-fail/45 bg-fail/[.07] px-3 py-2.5 text-sm">
      <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold tracking-[.06em] text-fail">
        <Icon name="state-fail" size={12} />
        COULDN’T LOAD
      </span>
      <span className="min-w-0 font-mono text-xs break-words text-text-soft">{text}</span>
      {onRetry && (
        <Button size="sm" onClick={onRetry} disabled={busy} className="ml-auto max-sm:min-h-[44px]">
          {busy ? 'Loading…' : 'Try again'}
        </Button>
      )}
    </div>
  )
}

export function SkeletonRows({ rows = 5, cols = 6 }: { rows?: number; cols?: number }) {
  return (
    <div role="status" aria-busy="true" aria-label="Loading" className="flex flex-col gap-2">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {Array.from({ length: cols }, (_, j) => (
            <span key={j} className="h-4 animate-breathe rounded-sm bg-line" style={{ width: `${50 + ((i * 7 + j * 13) % 45)}%` }} />
          ))}
        </div>
      ))}
    </div>
  )
}

/* ── platforms ────────────────────────────────────────────────────────────── */

const PLATFORMS: Platform[] = ['youtube', 'twitch', 'kick', 'facebook', 'instagram']

/** Destination profile → platform of the kit ('custom' and the rest → RTMP). */
export const platformOf = (profile: string): Platform => (PLATFORMS.includes(profile as Platform) ? (profile as Platform) : 'rtmp')

/** Platform slot: monogram until the official one-colour SVG lands (kit §2 "Logos"). */
export function PlatSlot({ p, size = 22, dim }: { p: Platform; size?: number; dim?: boolean }) {
  return (
    <span
      title={PLATFORM_LABEL[p]}
      aria-label={PLATFORM_LABEL[p]}
      role="img"
      className={`grid flex-none place-items-center rounded-sm border border-slot font-mono font-medium text-text ${dim ? 'opacity-[.45]' : ''}`}
      style={{ width: size, height: size, fontSize: size < 26 ? 9 : 11 }}
    >
      {p === 'rtmp' ? <RtmpIcon size={Math.round(size * 0.6)} /> : MONO[p]}
    </span>
  )
}
